
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import Profile from "./Pages/Profile";
import AuthPage from "./Pages/AuthPage";
import Home from "./Pages/Home";
import Footer from "./Pages/Footer";
import ChatBox from "./Pages/ChatBox";
import ProfileEdit from "./components/ProfileInfo/ProfileEdit";
import PrivateRoute from "./components/AuthPage/PrivateRoute";
import SearchPage from "./Pages/SearchPage";
import PostDetailPage from "./Pages/PostDetailPage";
import CreaterDashboard from "./Pages/CreaterDashBoard";
import DashboardPage from "./Pages/DashboardPage";
import AdminDashboardPage from "./Pages/AdminDashboardPage";
import NotificationsPage from "./Pages/NotificationsPage";
import { AuthProvider, useAuth } from "./components/AuthPage/AuthContext";
import LandingPage from "./Pages/LandingPage/LandingPage";
import { useEffect } from "react";
import { trackEvent } from "./utils/analytics";

// Full-width layout for Feed — no max-w constraint, keeps sidebar offset + footer
function FeedLayout() {
  const { token } = useAuth();
  return (
    <div className="h-screen bg-black text-white overflow-hidden">
      <div className="h-screen md:pl-16">
        <Outlet />
      </div>
      {token && <Footer />}
    </div>
  );
}

// Layout wrapper for pages that use the centered container + footer
function PageLayout() {
  const { token } = useAuth();
  const location = useLocation();

  const authRoutes = ["/auth", "/login", "/signup", "/register"];
  const isAuthPage = authRoutes.includes(location.pathname);
  const showFooter = token && !isAuthPage;

  return (
    <div className="h-screen bg-black text-white flex flex-col overflow-hidden">
      <main
        className={`flex-1 overflow-y-auto md:pl-16 ${showFooter ? "pb-16 md:pb-0" : ""}`}
        style={{ height: showFooter ? "calc(100vh - 64px)" : "100vh" }}
      >
        <div className="w-full max-w-2xl mx-auto">
          <Outlet />
        </div>
      </main>
      {showFooter && <Footer />}
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center gap-3">
      <p className="text-white/30 text-4xl font-bold">404</p>
      <p className="text-white/20 text-sm">Page not found</p>
      <a href="/home" className="text-blue-500/60 hover:text-blue-400 text-sm transition">
        Go home
      </a>
    </div>
  );
}

function SessionTracker() {
  useEffect(() => {
    trackEvent('session_start');
    const onUnload = () => trackEvent('session_end');
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);
  return null;
}

function AppRoutes() {
  const { token } = useAuth();

  return (
    <>
      <SessionTracker />
      <Routes>
        {/* ChatBox — full width, no container wrapper */}
        <Route path="/chatbox" element={<PrivateRoute><ChatBox /></PrivateRoute>} />

        {/* Landing page — full width, no container wrapper */}
        <Route path="/" element={token ? <Navigate to="/home" /> : <LandingPage />} />

        {/* Auth page — full width, no container wrapper */}
        <Route path="/auth" element={<AuthPage />} />

        {/* Feed — full width, no max-w constraint */}
        <Route element={<FeedLayout />}>
          <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
        </Route>

        {/* All other pages — centered container + footer */}
        <Route element={<PageLayout />}>
          <Route path="/profile/:userId" element={<PrivateRoute><Profile /></PrivateRoute>} />
          <Route path="/ProfileEdit" element={<PrivateRoute><ProfileEdit /></PrivateRoute>} />
          <Route path="/CreaterDashboard" element={<PrivateRoute><CreaterDashboard /></PrivateRoute>} />
          <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />
          <Route path="/post/:id"   element={<PrivateRoute><PostDetailPage /></PrivateRoute>} />
          <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/admin-dashboard" element={<PrivateRoute><AdminDashboardPage /></PrivateRoute>} />
          <Route path="/notifications" element={<PrivateRoute><NotificationsPage /></PrivateRoute>} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
}

export default App;
